import { useEffect, useRef } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { Lemonade, units } from "../internal/types";
import "../styles/NewLemonadeForm.css";
import { useLemonadeContext } from "@/LemonadeProvider";
import { MinusIcon, PlusIcon } from "lucide-react";
import { UnitOptions } from "@/components/UnitOptions";

export function NewLemonadeForm() {
    const {
        control,
        register,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors }
    } = useForm<Lemonade>({ shouldFocusError: false });
    const { fields, append, remove } = useFieldArray({ control, name: "ingredients" });
    const { addLemonade, ingredients, getUnitsForIngredient } = useLemonadeContext();

    const appendedRef = useRef(false);
    useEffect(() => {
        if (fields.length === 0 && !appendedRef.current) {
            append({ name: "víz", amount: { amount: 1, unit: "l" } });
            appendedRef.current = true; // prevents appending again on re-render
        }
    }, []);

    const onSubmit = async (data: Lemonade) => {
        const result = await addLemonade(data);
        if (typeof result === "string") {
            alert(result);
        } else {
            alert("Limonádé sikeresen hozzáadva!");
            reset();
            remove();
            append({ name: "víz", amount: { amount: 1, unit: "l" } });
        }
    };

    const showErrors = (index: number) => {
        return (
            errors.ingredients?.[index]?.name != null ||
            errors.ingredients?.[index]?.amount?.amount != null ||
            errors.ingredients?.[index]?.amount?.unit != null
        );
    };

    const watchedIngredients = watch("ingredients");

    return (
        <form onSubmit={handleSubmit(onSubmit)} noValidate={false} className="lemonade-form">
            <h1 className="main-title">Új limonádé</h1>
            <input
                {...register("name", {
                    required: "A név kötelező",
                    pattern: {
                        value: /^[\p{L}\s]+$/u,
                        message: "A limonádé neve csak betűket és szóközt tartalmazhat"
                    },
                    validate: (value) => value.trim().length > 0 || "Nem lehet üres a név"
                })}
                placeholder="Limonádé neve"
            />
            {errors.name && <span className="error-message">{errors.name.message}</span>}

            <input
                type="number"
                {...register("price", {
                    required: "Az ár kötelező",
                    valueAsNumber: true,
                    min: { value: 1, message: "Az árnak legalább 1 forintnak kell lennie" },
                    validate: (value) => Number.isInteger(value) || "Az ár csak egész szám lehet"
                })}
                placeholder="Egységár (Ft / l)"
                min="1"
                step="1"
            />
            {errors.price && <span className="error-message">{errors.price.message}</span>}

            <div className="divider" />

            <div className="ingredients-header">
                <h2>Hozzávalók</h2>
                <button type="button" onClick={() => append({ name: "", amount: { amount: 1, unit: "l" } })} className="image-button">
                    <PlusIcon strokeWidth={"2.5px"} />
                </button>
            </div>

            {fields.map((field, index) => (
                <div key={field.id} className="ingredient-item">
                    <input
                        {...register(`ingredients.${index}.name`, {
                            required: "A hozzávaló neve kötelező",
                            pattern: {
                                value: /^[\p{L}\s]+$/u,
                                message: "A hozzávaló neve csak betűket és szóközt tartalmazhat"
                            },
                            validate: (value) => value.trim().length > 0 || "Nem lehet üres a hozzávaló neve"
                        })}
                        placeholder="Hozzávaló"
                        list="ingredient-list"
                        onChange={(e) => {
                            register(`ingredients.${index}.name`).onChange(e);
                            const validUnits = getUnitsForIngredient(e.target.value);
                            setValue(`ingredients.${index}.amount.unit`, validUnits[0]);
                        }}
                    />

                    <datalist id="ingredient-list">
                        {[...ingredients].map((name) => (
                            <option key={name} value={name} />
                        ))}
                    </datalist>

                    <input
                        {...register(`ingredients.${index}.amount.amount`, {
                            required: "A hozzávaló mennyisége kötelező",
                            valueAsNumber: true,
                            min: { value: 0.01, message: "A mennyiségnek legalább 0.01-nek kell lennie" }
                        })}
                        placeholder="Mennyiség"
                        step="0.01"
                        type="number"
                    />

                    <select
                        {...register(`ingredients.${index}.amount.unit`, {
                            validate: (value) => units.includes(value) || "Érvénytelen mértékegység"
                        })}
                    >
                        <UnitOptions ingredient={watchedIngredients?.[index]?.name ?? ""} />
                    </select>

                    {
                        /* Ignore remove button for first ingredient*/
                        index != 0 && (
                            <button type="button" onClick={() => remove(index)} className="remove-button image-button">
                                <MinusIcon />
                            </button>
                        )
                    }
                    <div className="error-message" style={{ display: showErrors(index) ? "flex" : "none" }}>
                        {errors.ingredients?.[index]?.name && <span>{errors.ingredients[index].name.message}</span>}
                        {errors.ingredients?.[index]?.amount?.amount && <span>{errors.ingredients[index].amount.amount.message}</span>}
                        {errors.ingredients?.[index]?.amount?.unit && <span>{errors.ingredients[index].amount.unit.message}</span>}
                    </div>
                </div>
            ))}

            <input
                type="hidden"
                {...register("ingredients", { validate: (value) => value.length > 0 || "Legalább egy hozzávaló szükséges" })}
            />
            {errors.ingredients && <span className="error-message">{errors.ingredients.message}</span>}

            <div className="submit-row">
                <button type="submit">Létrehozás</button>
            </div>
        </form>
    );
}
